
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '../utils/loggingUtils';

const logger = createLogger('sessionValidator');

// Cache for session status to prevent redundant checks
const sessionStatusCache = {
  valid: false,
  expiresIn: 0,
  timestamp: 0
};

// Cache expiry in milliseconds (30 seconds)
const CACHE_EXPIRY = 30 * 1000;

// Check and log session status - useful for debugging
export const checkSessionStatus = async (): Promise<{ valid: boolean, expiresIn?: number }> => {
  // Check cache first to prevent redundant API calls
  const now = Date.now();
  if (now - sessionStatusCache.timestamp < CACHE_EXPIRY) {
    logger.debug('Using cached session status');
    return { valid: sessionStatusCache.valid, expiresIn: sessionStatusCache.expiresIn };
  }

  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      logger.error('Error checking session status:', error);
      return { valid: false };
    }
    
    if (!data.session) {
      logger.debug('No session found in status check'); // Changed from warn to debug
      return { valid: false };
    }
    
    const expiresAt = data.session.expires_at;
    if (expiresAt) {
      const nowSeconds = Math.floor(Date.now() / 1000);
      const expiresIn = expiresAt - nowSeconds;
      logger.debug(`Session valid, expires in ${expiresIn}s`); // Changed from info to debug
      
      // Update cache
      sessionStatusCache.valid = true;
      sessionStatusCache.expiresIn = expiresIn;
      sessionStatusCache.timestamp = now;
      
      return { valid: true, expiresIn };
    }
    
    logger.debug('Session valid, but no expiry information'); // Changed from info to debug
    
    // Update cache
    sessionStatusCache.valid = true;
    sessionStatusCache.timestamp = now;
    
    return { valid: true };
  } catch (error) {
    logger.error('Exception checking session status:', error);
    return { valid: false };
  }
};

// Check if a session is expiring soon
// Using optional parameter for threshold (in seconds)
export const isSessionExpiringSoon = async (thresholdSeconds: number = 300): Promise<boolean> => {
  try {
    // Check cache first to prevent redundant API calls
    const now = Date.now();
    if (now - sessionStatusCache.timestamp < CACHE_EXPIRY && sessionStatusCache.valid) {
      if (sessionStatusCache.expiresIn !== undefined && sessionStatusCache.expiresIn < thresholdSeconds) {
        return true;
      } else if (sessionStatusCache.valid) {
        return false;
      }
    }
    
    const { valid, expiresIn } = await checkSessionStatus();
    
    if (!valid) {
      return true; // Not valid means we need a refresh
    }
    
    if (expiresIn !== undefined && expiresIn < thresholdSeconds) {
      // Less than threshold until expiry
      logger.debug(`Session expires soon (${expiresIn}s)`); // Changed from info to debug
      return true;
    }
    
    return false;
  } catch (error) {
    logger.error('Error checking if session is expiring soon:', error);
    return true; // Default to needing refresh on error
  }
};

// Ensure session is still valid
export const validateSession = async (): Promise<boolean> => {
  try {
    // Check cache first
    if (Date.now() - sessionStatusCache.timestamp < CACHE_EXPIRY) {
      return sessionStatusCache.valid;
    }
    
    const { valid } = await checkSessionStatus();
    return valid;
  } catch (error) {
    logger.error('Error validating session:', error);
    return false;
  }
};
